import { HttpError } from './HttpError';
import { ResponseType } from './responseType';

enum HttpStatusCode {
  BadRequest = 400,
  InternalServerError = 500,
  NoContent = 204,
  NotFound = 404,
  Ok = 200,
}

class Http {
  public async get<T = Record<string, unknown>>(url: string, responseType?: ResponseType): Promise<T> {
    return await this.makeRequest('GET', url, undefined, responseType);
  }

  public async post<T = Record<string, unknown>>(
    url: string,
    data?: Record<string, unknown> | unknown
  ): Promise<T> {
    return await this.makeRequest('POST', url, data);
  }

  public async put<T = Record<string, unknown>>(
    url: string,
    data?: Record<string, unknown> | unknown
  ): Promise<T> {
    return await this.makeRequest('PUT', url, data);
  }

  public async delete<T = Record<string, unknown>>(
    url: string,
    data?: Record<string, unknown> | unknown
  ): Promise<T> {
    return await this.makeRequest('DELETE', url, data);
  }

  public async patch<T = Record<string, unknown>>(
    url: string,
    data?: Record<string, unknown> | unknown
  ): Promise<T> {
    return await this.makeRequest('PATCH', url, data);
  }

  private getOptions(method: string) {
    const antiForgeryToken = (document.getElementsByName('__RequestVerificationToken')[0] as HTMLInputElement)?.value;

    return {
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-TOKEN': antiForgeryToken,
      },
      method,
    };
  }

  private async makeRequest<T = Record<string, unknown>>(
    method: 'DELETE' | 'GET' | 'POST' | 'PUT' | 'PATCH',
    url: string = '',
    body?: Record<string, unknown> | string | unknown,
    responseType?: ResponseType
  ): Promise<T> {
    const options: RequestInit = this.getOptions(method);

    if (body) {
      options.body = JSON.stringify(body);
    }

    const response = await fetch(url, options);
    
    if (response.status === HttpStatusCode.NoContent) {
      return {} as T;
    }
    
    if (response.ok) {
      if (responseType === ResponseType.ArrayBuffer) {
        return response.arrayBuffer() as Promise<T>;
      }
      
      if (responseType === ResponseType.Blob) {
        const out = await response.blob();
        return { data: out, response } as T;
      }
      
      return response.json() as Promise<T>;
    } else {
      if (response.headers.get('Content-Type')?.includes('application/json')) {
        const value = await response.json();
        throw new HttpError(response.status, `${ value.message }`, { cause: value });
      }
      
      throw new HttpError(response.status, `${ value.message }`);
    }
  
}
}

export const http = new Http();